import { useState, useEffect, useCallback, useRef } from 'react';

// Global flag to disable history updates if we detect excessive usage
let isHistoryGloballyDisabled = false;
let globalDisableTime = 0;

// Reset global disable flag after 30 seconds
setInterval(() => {
  if (isHistoryGloballyDisabled && Date.now() - globalDisableTime > 30000) {
    isHistoryGloballyDisabled = false;
    console.log('History API calls re-enabled after cooldown period');
  }
}, 5000);

interface FilterState {
  query?: string;
  age?: string;
  style?: string;
  language?: string;
  favorites?: boolean;
  view?: 'grid' | 'bookshelf';
}

export function useQueryParams() {
  const [params, setParams] = useState<FilterState>({});
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const isUpdatingRef = useRef<boolean>(false);
  const pendingUpdateRef = useRef<Partial<FilterState> | null>(null);
  const historyCallCountRef = useRef<number>(0);
  const historyCallResetRef = useRef<number>(Date.now());
  const isHistoryBlockedRef = useRef<boolean>(false);

  // Parse query params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialState: FilterState = {};

    const query = urlParams.get('query');
    const age = urlParams.get('age');
    const style = urlParams.get('style');
    const language = urlParams.get('language');
    const favorites = urlParams.get('favorites');
    const view = urlParams.get('view');

    if (query) initialState.query = query;
    if (age) initialState.age = age;
    if (style) initialState.style = style;
    if (language) initialState.language = language;
    if (favorites === 'true') initialState.favorites = true;
    if (view === 'grid' || view === 'bookshelf') initialState.view = view;

    setParams(initialState);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to safely update history with circuit breaker
  const safeHistoryUpdate = useCallback((newUrl: string) => {
    // Check global disable flag first
    if (isHistoryGloballyDisabled) {
      return false;
    }
    
    // In development, be more aggressive about blocking history updates
    if (process.env.NODE_ENV === 'development') {
      const now = Date.now();
      
      // Reset counter every 5 seconds in development
      if (now - historyCallResetRef.current > 5000) {
        historyCallCountRef.current = 0;
        historyCallResetRef.current = now;
        isHistoryBlockedRef.current = false;
      }
      
      // Circuit breaker: block if more than 10 calls in 5 seconds in development
      if (historyCallCountRef.current > 10) {
        isHistoryBlockedRef.current = true;
        isHistoryGloballyDisabled = true;
        globalDisableTime = Date.now();
        console.warn('History API calls globally disabled due to excessive usage in development');
        return false;
      }
    } else {
      const now = Date.now();
      
      // Reset counter every 10 seconds in production
      if (now - historyCallResetRef.current > 10000) {
        historyCallCountRef.current = 0;
        historyCallResetRef.current = now;
        isHistoryBlockedRef.current = false;
      }
      
      // Circuit breaker: block if more than 50 calls in 10 seconds in production
      if (historyCallCountRef.current > 50) {
        isHistoryBlockedRef.current = true;
        console.warn('History API calls blocked due to excessive usage');
        return false;
      }
    }
    
    // If blocked, don't make the call
    if (isHistoryBlockedRef.current) {
      return false;
    }
    
    try {
      window.history.replaceState({}, '', newUrl);
      historyCallCountRef.current++;
      return true;
    } catch (error) {
      console.warn('Failed to update URL:', error);
      isHistoryBlockedRef.current = true;
      // In development, disable globally on error
      if (process.env.NODE_ENV === 'development') {
        isHistoryGloballyDisabled = true;
        globalDisableTime = Date.now();
      }
      return false;
    }
  }, []);

  // Update URL when params change
  const updateParams = useCallback((newParams: Partial<FilterState>) => {
    // If already updating, store the pending update
    if (isUpdatingRef.current) {
      pendingUpdateRef.current = { ...pendingUpdateRef.current, ...newParams };
      return;
    }

    setParams(prev => {
      const updated = { ...prev, ...newParams };
      
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Rate limiting: only allow updates every 500ms (increased from 100ms)
      const now = Date.now();
      if (now - lastUpdateRef.current < 500) {
        pendingUpdateRef.current = { ...pendingUpdateRef.current, ...newParams };
        updateTimeoutRef.current = setTimeout(() => {
          if (pendingUpdateRef.current) {
            updateParams(pendingUpdateRef.current);
            pendingUpdateRef.current = null;
          }
        }, 500 - (now - lastUpdateRef.current));
        return updated;
      }
      
      lastUpdateRef.current = now;
      isUpdatingRef.current = true;
      
      // Use setTimeout to prevent infinite loops from rapid state updates
      updateTimeoutRef.current = setTimeout(() => {
        try {
          // Build new URL params
          const urlParams = new URLSearchParams();
          
          if (updated.query && updated.query.trim()) {
            urlParams.set('query', updated.query);
          }
          if (updated.age && updated.age !== 'all') {
            urlParams.set('age', updated.age);
          }
          if (updated.style && updated.style !== 'all') {
            urlParams.set('style', updated.style);
          }
          if (updated.language && updated.language !== 'all') {
            urlParams.set('language', updated.language);
          }
          if (updated.favorites) {
            urlParams.set('favorites', 'true');
          }
          if (updated.view && updated.view !== 'bookshelf') {
            urlParams.set('view', updated.view);
          }

          // Update browser URL without page reload
          const newUrl = urlParams.toString() 
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          
          // Only update URL if it's different from current URL
          const currentUrl = window.location.href.split('?')[0] + (window.location.search || '');
          if (newUrl !== currentUrl) {
            safeHistoryUpdate(newUrl);
          }
        } catch (error) {
          console.warn('Failed to update URL:', error);
        } finally {
          isUpdatingRef.current = false;
          // Process any pending updates
          if (pendingUpdateRef.current) {
            const pending = pendingUpdateRef.current;
            pendingUpdateRef.current = null;
            setTimeout(() => updateParams(pending), 100);
          }
        }
      }, 100); // Increased timeout for better stability
      
      return updated;
    });
  }, []);

  // Clear all params
  const clearParams = useCallback(() => {
    // If already updating, skip this clear operation
    if (isUpdatingRef.current) {
      return;
    }

    setParams({});
    
    // Clear any existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Rate limiting: only allow updates every 500ms
    const now = Date.now();
    if (now - lastUpdateRef.current < 500) {
      updateTimeoutRef.current = setTimeout(() => {
        clearParams();
      }, 500 - (now - lastUpdateRef.current));
      return;
    }
    
    lastUpdateRef.current = now;
    isUpdatingRef.current = true;
    
    // Use setTimeout to prevent rapid history updates
    updateTimeoutRef.current = setTimeout(() => {
      try {
        safeHistoryUpdate(window.location.pathname);
      } catch (error) {
        console.warn('Failed to clear URL:', error);
      } finally {
        isUpdatingRef.current = false;
      }
    }, 100);
  }, []);

  // Get search filters only (excluding view and favorites)
  const getSearchFilters = useCallback(() => {
    const filters: { query?: string; age?: string; style?: string; language?: string } = {};
    
    if (params.query && params.query.trim()) {
      filters.query = params.query;
    }
    if (params.age && params.age !== 'all') {
      filters.age = params.age;
    }
    if (params.style && params.style !== 'all') {
      filters.style = params.style;
    }
    if (params.language && params.language !== 'all') {
      filters.language = params.language;
    }
    
    return filters;
  }, [params.query, params.age, params.style, params.language]);

  return {
    params,
    updateParams,
    clearParams,
    getSearchFilters,
  };
}